<template>
  <div class="company-culture-chart">
    <loading-indicator v-if="loading" align-middle/>
    <v-responsive v-if="!loading && cultureData" :aspect-ratio="1" style="width: 85%; margin: auto;">
      <svg :viewBox="`0 0 ${maxValue*2} ${maxValue*2}`" xmlns="http://www.w3.org/2000/svg">

        <template
          v-for="k in rects">
          <g v-if="cultureData" :key="k.type" :class="`${selected === k.type && 'hovered'}`" class="culture-data-g">
            <rect
              :height="cultureData[k.type]"
              :style="{fill: k.fill}"
              :width="cultureData[k.type]"
              :x="k.x" :y="k.y" class="culture-data-rect">
            </rect>
            <svg
              :height="cultureData[k.type] + k.plus"
              :width="cultureData[k.type] + k.plus"
              :x="k.tx"
              :y="k.ty">
              <text :style="`${k.plus > 0 && 'fill: #000'}`" dominant-baseline="middle" text-anchor="middle" x="50%"
                    y="50%">
                {{ Math.round(cultureData[k.type]) }}%
              </text>
            </svg>
          </g>
        </template>


        <line :x1="maxValue" :x2="maxValue" :y2="maxValue*2 - 10" stroke="black" y1="10"></line>
        <polygon
          :points="`${maxValue},${8} ${maxValue - 2},${11} ${maxValue + 2},${11}`"
          style="fill: #000"></polygon>
        <line :x2="maxValue*2 - 10" :y1="maxValue" :y2="maxValue" stroke="black" x1="10"></line>
        <polygon
          :points="`${maxValue*2 - 8},${maxValue} ${maxValue*2 - 11},${maxValue - 2} ${maxValue*2 - 11},${maxValue + 2}`"
          style="fill: #000"></polygon>

        <text :x="maxValue + 4" :y="12"
              style="fill: #000; writing-mode: tb; font-size: 4px; letter-spacing: 0.2;">
          {{ $t('reports.capability') }}
        </text>
        <text :x="maxValue*2 - 12" :y="maxValue - 3"
              style="fill: #000; font-size: 4px; letter-spacing: 0.2;"
              text-anchor="end">
          {{ $t('reports.efficiency') }}
        </text>

        <text :x="2" :y="2"
              class="category-text"
              dominant-baseline="hanging"
              text-anchor="start">
          {{ $t('reports.culture_categories.individual') }}
        </text>
        <text :x="maxValue*2 - 2" :y="2"
              class="category-text"
              dominant-baseline="hanging"
              text-anchor="end">
          {{ $t('reports.culture_categories.productive') }}
        </text>
        <text :x="2" :y="maxValue*2 - 2"
              class="category-text"
              dominant-baseline="auto"
              text-anchor="start">
          {{ $t('reports.culture_categories.fragmented') }}
        </text>
        <text :x="maxValue*2 - 2" :y="maxValue*2 - 2"
              class="category-text"
              dominant-baseline="auto"
              text-anchor="end">
          {{ $t('reports.culture_categories.authoritative') }}
        </text>
        <template
          v-for="k in rects">
          <rect :key="`culture-rect-${k.type}`" :class="`culture-rect culture-rect-${k.type}`" :height="maxValue"
                :width="maxValue"
                :x="k.rx" :y="k.ry" fill="transparent" stroke="transparent" stroke-width="0">
          </rect>
        </template>
      </svg>
    </v-responsive>
    <!--<v-row v-if="!loading && cultureData">
      <v-col class="text-right" cols="12">
        <span class="caption">{{ $formatDateTime(this.calculation.updatedAt) }}</span>
      </v-col>
    </v-row>-->
    <v-row v-if="!loading && !cultureData">
      <v-col cols="12">
        <no-content-view :description="$t('reports.personnel.no_data_help')"
                         :description-extra="$t('reports.personnel.no_data_help2')"
                         :function-button-text="$t('refresh')"
                         :propFunction="() => $emit('refresh')"
                         :reload-button="false" :title="$t('reports.personnel.no_data')"
                         function-button
                         function-button-icon="mdi mdi-refresh"
        ></no-content-view>
      </v-col>
    </v-row>
    <template
      v-for="k in rects">
      <v-tooltip :key="`hover-element-${k.type}`" :activator="`.culture-rect-${k.type}`" top>
        <div v-if="cultureData" style="max-width: 300px"
             v-html="`${$t(`reports.culture_instructions.${k.type}`)} <br /> <strong>${Math.round(cultureData[k.type])} %</strong>`"></div>
      </v-tooltip>
    </template>
  </div>
</template>

<script>
import NoContentView from "@/components/NoContentView";
import LoadingIndicator from "@/components/LoadingIndicator";

export default {
  name: "CompanyCultureChart",
  components: {LoadingIndicator, NoContentView},
  props: ['calculation', 'loading'],
  data() {
    return {
      selected: null,
    }
  },
  methods: {
    magic(/*type*/) {
      //this.selected = type;
    },
    magicOff() {
      // this.selected = null;
    }
  },
  computed: {
    cultureData() {
      const culture = this.calculation?.engineResponse?.lta?.culture;
      if (!culture) {
        return null;
      }
      const cData = {
        individual: null,
        productive: null,
        fragmented: null,
        authoritative: null,
      }
      culture.forEach(c => {
        switch (c.name) {
          case 'YKSILÖLLINEN':
            cData.individual = c.value;
            break;
          case 'AIKAANSAAVA':
            cData.productive = c.value;
            break;
          case 'HAJANAINEN':
            cData.fragmented = c.value;
            break;
          case 'VALLANKÄYTTÖ':
            cData.authoritative = c.value;
            break;
        }
      });

      // PORT-460 Culture category values cannot be over 100%
      // Look for the smallest decimal and remove 1 from it until the sum of all values is 100
      while ([cData.individual, cData.productive, cData.fragmented, cData.authoritative].reduce((previousValue, currentValue) => previousValue + Math.round(currentValue), 0) > 100) {

        // Get the index of smallest decimal value
        const minIndex = [cData.individual, cData.productive, cData.fragmented, cData.authoritative].reduce((iMin, x, i, arr) => x % 1 < arr[iMin] % 1 ? i : iMin, 0);
        if(minIndex === 0 && cData.individual >= 1){
          cData.individual--;
        } else if(minIndex === 1 && cData.productive >= 1){
          cData.productive--;
        } else if(minIndex === 2 && cData.fragmented >= 1){
          cData.fragmented--;
        } else if(minIndex === 3 && cData.authoritative >= 1){
          cData.authoritative--;
        }else{
          if(cData.individual >= 1){
            cData.individual--;
          } else if(cData.productive >= 1){
            cData.productive--;
          } else if(cData.fragmented >= 1){
            cData.fragmented--;
          } else if(cData.authoritative >= 1){
            cData.authoritative--;
          }
        }
      }

      return cData;
    },
    maxValue() {
      let maxValue = Math.max(this.cultureData?.individual, this.cultureData?.productive, this.cultureData?.fragmented, this.cultureData?.authoritative);
      if (maxValue < 50) maxValue = 50;
      return maxValue + 30;
    },
    rects() {
      const maxValue = this.maxValue
      return [
        {type: 'individual', fill: this.$theme.opportunity},
        {type: 'productive', fill: this.$theme.strength},
        {type: 'fragmented', fill: this.$theme.error},
        {type: 'authoritative', fill: this.$theme.opportunity}].map(r => {
        r.plus = this.cultureData?.[r.type] > 17 ? 0 : 22;
        return r;
      }).map(r => {
        const value = this.cultureData?.[r.type]
        switch (r.type) {
          case 'individual':
            r.tx = maxValue - (value + r.plus);
            r.ty = maxValue - (value + r.plus);
            r.x = maxValue - value;
            r.y = maxValue - value;
            r.rx = 0;
            r.ry = 0;
            break;
          case 'productive':
            r.tx = maxValue;
            r.ty = maxValue - (value + r.plus);
            r.x = maxValue;
            r.y = maxValue - value;
            r.rx = maxValue;
            r.ry = 0;
            break;
          case 'fragmented':
            r.tx = maxValue - (value + r.plus);
            r.ty = maxValue;
            r.x = maxValue - value;
            r.y = maxValue;
            r.rx = 0;
            r.ry = maxValue;
            break;
          case 'authoritative':
            r.tx = maxValue;
            r.ty = maxValue;
            r.x = maxValue;
            r.y = maxValue;
            r.rx = maxValue;
            r.ry = maxValue;

            break;
        }
        r.value = this.cultureData?.[r.type];
        return r;
      });
    }
  }
}
</script>

<style lang="scss" scoped>
.company-culture-chart {
  width: 100%;
}

svg text {
  font-weight: bold;
  font-size: 8px;
  font-family: $body-font-family;
  text-transform: uppercase;
  fill: #fff;
}

svg .category-text {
  font-size: 6px;
  fill: #000;
}

/*svg .culture-data-g {
  transition: all 0.2s ease-in-out;
}

svg .culture-data-g.hovered {
  transform: scale(1.1) translate(-5%, -5%);
}*/

svg .culture-rect {
  border: 0;

  &:focus {
    stroke-width: 0;
    stroke: transparent;
    outline: none;
  }
}

@media screen and (max-width: 400px) {
  .company-culture-chart {
    .chart-section-title {
      font-size: 10px !important;
    }
  }
}

</style>
